import { useMemo } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Text } from '@rtkwlf/c3-core-react';
import {
  Timeline,
  TimelineGroup,
  TimelineGroupHeader,
  TimelineGroupItems,
} from '@rtkwlf/c3-recipes-timeline';
import { Button, Skeleton } from '@rtkwlf/fenrir-react';
import { faExclamationTriangle } from '@rtkwlf/pro-solid-svg-icons/faExclamationTriangle';
import { faRotateRight } from '@rtkwlf/pro-solid-svg-icons/faRotateRight';
import {
  SecurityFocusEventType,
  type SecurityFocusFull,
} from '@rtkwlf/track-public';
import { useQuery } from '@tanstack/react-query';

import { useGetOrganizationSecurityFocusEventsOptions } from '@apis/track-public/query-options';
import { arrayToKeyedObject } from '@packages/utils/array-to-keyed-object/array-to-keyed-object';

import { useSecurityJourneyTranslations } from '../../../../locales/use-security-journey-translations';

import {
  type SecurityFocusTimelineItemType,
  getSecurityFocusTimelineData,
} from './security-focus-timeline/get-security-focus-timeline-data';
import { SecurityFocusTimelineAdhoc } from './security-focus-timeline/security-focus-timeline-adhoc';
import { SecurityFocusTimelineBlueprint } from './security-focus-timeline/security-focus-timeline-blueprint';
import { SecurityFocusTimelineFocus } from './security-focus-timeline/security-focus-timeline-focus';
import { SecurityFocusTimelineSpidr } from './security-focus-timeline/security-focus-timeline-spidr';
import style from './security-focus-timeline.module.css';

/* -------------------------------------------------------------------------------------------------
 * Utilities and Constants
 * -----------------------------------------------------------------------------------------------*/
const getGroupHeaderProps = (
  name: string,
): {
  title: string;
  description: string;
  minCount: number;
  countType: SecurityFocusEventType;
} => {
  switch (name) {
    case 'instantiated': {
      return {
        title: 'focusID.timeline.started_completed_caps',
        description: 'focusID.timeline.spidr',
        minCount: 2,
        countType: SecurityFocusEventType.SpiDr,
      };
    }
    case 'upcoming': {
      return {
        title: 'focusID.timeline.upcoming_caps',
        description: 'focusID.timeline.spidr',
        minCount: 1,
        countType: SecurityFocusEventType.Blueprint,
      };
    }
    default: {
      return {
        title: 'focusID.timeline.unknown_caps',
        description: 'focusID.timeline.event',
        minCount: 1,
        countType: SecurityFocusEventType.SecurityFocus,
      };
    }
  }
};

/* -------------------------------------------------------------------------------------------------
 * SecurityFocusTimelineItem
 * -----------------------------------------------------------------------------------------------*/
type SecurityFocusTimelineItemProps = {
  focus: SecurityFocusFull;
  item: SecurityFocusTimelineItemType;
};

const SecurityFocusTimelineItem: React.FunctionComponent<
  SecurityFocusTimelineItemProps
> = ({ focus, item }) => {
  switch (item.type) {
    case SecurityFocusEventType.Blueprint: {
      return (
        <SecurityFocusTimelineBlueprint
          key={item.id}
          name={item.name}
          tier={item.tier}
        />
      );
    }
    case SecurityFocusEventType.Detour: {
      return (
        <SecurityFocusTimelineFocus
          key={item.id}
          focusID={item.id}
          type="detour"
        />
      );
    }
    case SecurityFocusEventType.Adhoc: {
      return <SecurityFocusTimelineAdhoc key={item.id} spidrID={item.id} />;
    }
    case SecurityFocusEventType.SpiDr: {
      return (
        <SecurityFocusTimelineSpidr
          key={item.id}
          expiryDate={item.expiryDate}
          name={item.blueprint.name}
          spidrID={item.id}
          state={item.state}
          ticketId={item.ticketId}
          focusID={focus.id}
          tier={item.blueprint.tier}
        />
      );
    }
    case SecurityFocusEventType.SecurityFocus: {
      return (
        <SecurityFocusTimelineFocus
          key={item.id}
          focusID={item.id}
          type="focus"
        />
      );
    }
  }
};

/* -------------------------------------------------------------------------------------------------
 * SecurityFocusTimeline
 * -----------------------------------------------------------------------------------------------*/
type SecurityFocusTimelineProps = {
  focus: SecurityFocusFull;
};

export const SecurityFocusTimeline = ({
  focus,
}: SecurityFocusTimelineProps) => {
  /**
   * Init the translations
   */
  const { t } = useSecurityJourneyTranslations();

  /**
   * Get the events
   */
  const {
    isLoading: isLoadingEvents,
    isError: isErrorEvents,
    data: events,
    refetch,
  } = useQuery(
    useGetOrganizationSecurityFocusEventsOptions({
      focusID: focus.id,
    }),
  );

  /**
   * Get the timeline data
   */
  const timeline = useMemo(() => {
    if (!events) {
      return [];
    }

    return getSecurityFocusTimelineData({
      focus: {
        blueprints: arrayToKeyedObject(focus.blueprints, 'id'),
        spidrs: arrayToKeyedObject(focus.spidrs, 'id'),
      },
      events,
    });
  }, [events, focus]);

  /**
   * handle the loading state
   */
  if (isLoadingEvents) {
    return (
      <div
        className={style['sft']}
        data-testid="security-focus-timeline-loading"
      >
        <Skeleton className={style['sft-loading']} />
      </div>
    );
  }

  /**
   * handle the error state
   */
  if (isErrorEvents) {
    return (
      <div className={style['sft']} data-testid="security-focus-timeline-error">
        <div className={style['sft-error']}>
          <FontAwesomeIcon icon={faExclamationTriangle} size="2x" />
          <Text contrast="medium">{t('focusID.timeline.error')}</Text>
          <Button
            iconLeft={<FontAwesomeIcon icon={faRotateRight} />}
            variant="link"
            onClick={() => refetch()}
          >
            {t('common.retry')}
          </Button>
        </div>
      </div>
    );
  }

  /**
   * Return the timeline
   */
  return (
    <div className={style['sft']} data-testid="security-focus-timeline">
      <Timeline>
        {timeline.map((item, j) => {
          switch (item.eventType) {
            case 'GROUP': {
              const props = getGroupHeaderProps(item.name);
              const spidrCount = item.items.filter(
                (f) => f.type === props.countType,
              ).length;
              return (
                <TimelineGroup
                  key={`${item.name}-${j}`}
                  data-testid={`security-focus-timeline-group-${item.name}`}
                >
                  <TimelineGroupHeader
                    title={t(props.title)}
                    description={t(props.description, {
                      count: spidrCount,
                    })}
                  />
                  <TimelineGroupItems
                    variant={
                      item.items.length <= props.minCount ? 'fade' : 'solid'
                    }
                  >
                    {item.items.map((i, k) => {
                      return (
                        <SecurityFocusTimelineItem
                          focus={focus}
                          key={`${i.id}-${k}`}
                          item={i}
                        />
                      );
                    })}
                  </TimelineGroupItems>
                </TimelineGroup>
              );
            }
            case 'ITEM': {
              return (
                <SecurityFocusTimelineItem
                  key={`${item.id}-${j}`}
                  focus={focus}
                  item={item}
                />
              );
            }
          }
        })}
      </Timeline>
    </div>
  );
};
